import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryParam, NumberParam } from 'use-query-params';
import { useProjectQuantAnalysisQuery } from '@trinity/chat-components/hooks/useProjectQuantAnalysisQuery';
import { useProjectQuantAnalysisVersionsQuery } from '@trinity/chat-components/hooks/useProjectQuantAnalysisVersionQuery';
import { useSuspendInitialLoading } from '@trinity/chat-components/hooks/useSuspendInitialLoading';
import {
  QuantAnalysisItemsContext,
  QuantAnalysisLoadingContext,
  QuantAnalysisContext,
  SelectedQuantAnalysisItemContext,
  SetSelectedQuantAnalysisItemContext,
  QuantAnalysisVersionsContext,
  QuantAnalysisVersionContext,
  FolderCreationModeContext,
  SetFolderCreationModeContext,
} from './context';

type Props = {
  projectId: number;
  quantAnalysisId: number;
  versionNumber?: number;
} & ChildrenProps;

export const ProjectQuantAnalysisContainer = (props: Props) => {

  const [selectedItemId, setSelectedItemId] = useQueryParam('analysisItemId', NumberParam);
  const [folderCreationMode, setFolderCreationMode] = useState(false);

  const updateSelectedItemId = useCallback((id: number) => {
    setSelectedItemId(id);
  }, [setSelectedItemId]);

  const query = useProjectQuantAnalysisQuery({
    projectId: props.projectId,
    quantAnalysisId: props.quantAnalysisId,
    versionNumber: props.versionNumber,
  }, {
    enabled: !Number.isNaN(props.quantAnalysisId),
    placeholderData: (previousData) => previousData || {
      analysis: null,
      version: null,
      items: [],
    },
  });

  const versionsQuery = useProjectQuantAnalysisVersionsQuery({
    projectId: props.projectId,
    quantAnalysisId: props.quantAnalysisId,
  }, {
    placeholderData: {
      versions: [],
      latestVersionId: null,
    },
  });

  useEffect(() => {
    if (query.data.items.length) {
      const firstItemId = query.data.items[0].id;
      if (!selectedItemId) {
        updateSelectedItemId(firstItemId);
      } else if (setSelectedItemId && !query.data.items.some(item => item.id === selectedItemId)) {
        // If the selected item ID is not in the current items, reset it to the first item
        updateSelectedItemId(firstItemId);
      }
    }
  }, [query.data.items, selectedItemId, updateSelectedItemId, setSelectedItemId]);

  const loading = useMemo(() => {
    return query.isLoading && !query.data;
  }, [
    query.isLoading,
    query.data,
  ]);

  const empty = useMemo(() => {
    return query.isFetchedAfterMount && !loading && !query.data.items.length;
  }, [
    query.isFetchedAfterMount,
    loading,
    query.data?.items,
  ]);

  const suspended = useSuspendInitialLoading({
    data: query.data.items,
    empty,
    isLoading: query.isLoading,
    loading,
  }, 500);

  return (
    <QuantAnalysisLoadingContext.Provider value={suspended.loading}>
      <SetSelectedQuantAnalysisItemContext.Provider value={updateSelectedItemId}>
        <SelectedQuantAnalysisItemContext.Provider value={selectedItemId}>
          <QuantAnalysisItemsContext.Provider value={suspended.data}>
            <QuantAnalysisContext.Provider value={query.data.analysis}>
              <QuantAnalysisVersionsContext.Provider value={versionsQuery.data.versions}>
                <QuantAnalysisVersionContext.Provider value={query.data.version}>
                  <FolderCreationModeContext.Provider value={folderCreationMode}>
                    <SetFolderCreationModeContext.Provider value={setFolderCreationMode}>
                      {props.children}
                    </SetFolderCreationModeContext.Provider>
                  </FolderCreationModeContext.Provider>
                </QuantAnalysisVersionContext.Provider>
              </QuantAnalysisVersionsContext.Provider>
            </QuantAnalysisContext.Provider>
          </QuantAnalysisItemsContext.Provider>
        </SelectedQuantAnalysisItemContext.Provider>
      </SetSelectedQuantAnalysisItemContext.Provider>
    </QuantAnalysisLoadingContext.Provider>
  );
};